// ─────────────────────────────────────────────────────────────────────
// FormulationImport — reusable importer for an existing flavor formulation.
// Ported from the Claude Design prototype (components/formulation-import.jsx),
// now wired to the real pre-create import pipeline.
//
// Flow:
//   1. Single dropzone — drop a spreadsheet, click to select, or paste rows
//      anywhere on the page.
//   2. Busy state — file upload (presign → S3 PUT) and/or server parse+resolve.
//   3. Resolution table — three columns: Raw name · Resolved ingredient
//      (live substance-search picker) · Dose (ppm). Rows add/remove freely.
//
// CONTROLLED:
//   value          — null while idle/busy, then { method, source, rows, … }
//   onChange(next) — set / clear the parsed import
//
// Wiring:
//   · paste  → parseImportFormulation({ kind:"paste", text })
//   · file   → useImportFileUpload (kind:import_formulation presign, no
//              flavor_formula_id) → parseImportFormulation({ kind:"file", id })
//   · picker → searchImportSubstances(q) → resolve a row to {ndbTable,rowId,…}
// On submit, CreateFlavorPage maps rows → finalizeFormulation.
// ─────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon/Icon";
import { useImportFileUpload } from "@/hooks/useImportFileUpload";
import {
  parseImportFormulation,
  searchImportSubstances,
  type ImportParseSource,
  type ResolvedSubstance,
} from "@/services/flavor_api";

export type FormulaImportRow = {
  id: string;
  raw: string;
  name?: string | null;
  cas?: string | null;
  fema?: string | null;
  ppm: number | "";
  /** Always "ppm" — server normalizes every convertible dose. */
  unit?: string;
  /** "unconvertible" when the source unit couldn't be interpreted. */
  doseStatus?: "ok" | "unconvertible";
  rawValue?: number | null;
  rawUnit?: string | null;
  resolved: ResolvedSubstance | null;
  added?: boolean;
};

export type FormulaImport = {
  method: "file" | "clipboard";
  source?: string;
  /** Set for file imports — threaded into finalizeFormulation for provenance. */
  sourceFileUploadId?: string | null;
  rows: FormulaImportRow[];
};

type FormulationImportProps = {
  value: FormulaImport | null;
  onChange: (next: FormulaImport | null) => void;
  onOpenLibrary?: () => void;
  /**
   * Tighten padding and typography for use inside dense drawers.
   * @default false
   */
  compact?: boolean;
  /**
   * Suppress the amber "unresolved"/"needs dose" warning boxes.
   * Useful when the host drawer manages its own validation gate.
   * @default false
   */
  hideUnresolvedWarning?: boolean;
};

const ACCEPTED = ".xlsx,.xls,.csv,.tsv,.txt";

// Browsers often report an empty file.type for .tsv/.csv. Derive a content type
// from the extension so it matches the server import_formulation allowlist +
// the S3 PUT Content-Type the presign hands back.
function contentTypeForFile(f: File): string {
  const lower = f.name.toLowerCase();
  if (lower.endsWith(".xlsx"))
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
  if (lower.endsWith(".csv")) return "text/csv";
  if (lower.endsWith(".tsv")) return "text/tab-separated-values";
  if (lower.endsWith(".txt")) return "text/plain";
  return f.type || "application/octet-stream";
}

export function FormulationImport({
  value,
  onChange,
  onOpenLibrary,
  compact = false,
  hideUnresolvedWarning = false,
}: FormulationImportProps) {
  const [drag, setDrag] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const upload = useImportFileUpload();
  const parsedForRef = useRef<string | null>(null);

  const snap = upload.snapshot;
  const uploadBusy = snap?.status === "presigning" || snap?.status === "uploading";
  const busy = parsing || uploadBusy;

  const runParse = useCallback(
    async (source: ImportParseSource, meta: Omit<FormulaImport, "rows">) => {
      setParsing(true);
      setError(null);
      try {
        const { rows } = await parseImportFormulation(source);
        const now = Date.now();
        onChange({
          ...meta,
          rows: rows.map((r, i) => ({
            id: `fr-${now}-${i}`,
            raw: r.raw ?? "",
            name: r.name ?? null,
            cas: r.cas ?? null,
            fema: r.fema ?? null,
            // Converted ppm → number; un-convertible → seed the raw number into
            // the editable field (rendered red); no amount at all → empty field.
            ppm:
              typeof r.ppm === "number"
                ? r.ppm
                : r.doseStatus === "unconvertible" && r.rawValue != null
                  ? r.rawValue
                  : "",
            unit: "ppm",
            doseStatus: r.doseStatus,
            rawValue: r.rawValue ?? null,
            rawUnit: r.rawUnit ?? null,
            resolved: r.resolved,
          })),
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Couldn't parse the formulation");
      } finally {
        setParsing(false);
      }
    },
    [onChange],
  );

  // After a file finishes uploading, parse it once.
  useEffect(() => {
    if (!snap) return;
    if (
      snap.status === "uploaded" &&
      snap.fileUploadId &&
      parsedForRef.current !== snap.fileUploadId
    ) {
      parsedForRef.current = snap.fileUploadId;
      void runParse(
        { kind: "file", fileUploadId: snap.fileUploadId },
        { method: "file", source: snap.filename, sourceFileUploadId: snap.fileUploadId },
      );
    } else if (snap.status === "failed") {
      setError(snap.errorMessage ?? "Upload failed");
    }
  }, [snap, runParse]);

  const startFile = useCallback(
    (f: File) => {
      const ct = contentTypeForFile(f);
      const typed = f.type === ct ? f : new File([f], f.name, { type: ct });
      upload.start(typed);
    },
    [upload],
  );

  // Paste anywhere on the page to import — active only while idle.
  useEffect(() => {
    if (value || busy) return;
    const onPaste = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData("text") ?? "";
      if (!text.trim()) return;
      void runParse({ kind: "paste", text }, { method: "clipboard" });
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [value, busy, runParse]);

  const reImport = () => {
    onChange(null);
    setError(null);
    parsedForRef.current = null;
    upload.reset();
  };

  // ── Busy state (upload and/or parse) ──
  if (busy) {
    const label = uploadBusy
      ? `Uploading ${snap?.filename ?? "file"}… ${snap?.progressPct ?? 0}%`
      : "Parsing your formulation…";
    return (
      <div
        className="vstack"
        style={{
          alignItems: "center",
          gap: compact ? 10 : 14,
          padding: compact ? "20px 16px" : "36px 20px",
          border: "1px solid var(--line)",
          borderRadius: 10,
          background: "var(--bg-sunk)",
        }}
      >
        <style>{"@keyframes fimSpin{to{transform:rotate(360deg)}}"}</style>
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: 999,
            flexShrink: 0,
            border: "3px solid var(--line-strong)",
            borderTopColor: "var(--accent-ink)",
            animation: "fimSpin 0.7s linear infinite",
          }}
        />
        <div className="vstack" style={{ gap: 4, alignItems: "center", textAlign: "center" }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: "var(--ink)" }}>{label}</div>
          <div style={{ fontSize: 11.5, color: "var(--ink-4)", lineHeight: 1.5, maxWidth: 360 }}>
            Reading rows and matching each ingredient against your Substance Library and our catalog.
          </div>
        </div>
      </div>
    );
  }

  // ── Resolved table ──
  if (value) {
    const rows = value.rows || [];
    const total = rows.reduce(
      (s, r) => s + (r.doseStatus === "unconvertible" ? 0 : Number(r.ppm) || 0),
      0,
    );
    const unresolved = rows.filter((r) => !r.resolved).length;
    // "" → no dose at all; a numeric ppm that is still flagged unconvertible is a
    // seeded raw value the user hasn't validated yet — both must block continuing.
    const needsDose = rows.filter(
      (r) => typeof r.ppm !== "number" || r.doseStatus === "unconvertible",
    ).length;

    const update = (next: FormulaImportRow[]) => onChange({ ...value, rows: next });
    const setResolved = (id: string, substance: ResolvedSubstance) =>
      update(rows.map((r) => (r.id === id ? { ...r, resolved: substance } : r)));
    const setPpm = (id: string, ppm: number | "") =>
      update(
        rows.map((r) =>
          r.id === id
            ? { ...r, ppm, doseStatus: "ok" as const, rawUnit: null, rawValue: null }
            : r,
        ),
      );
    const removeRow = (id: string) => update(rows.filter((r) => r.id !== id));
    const addRow = () =>
      update([
        ...rows,
        { id: `fr-new-${Date.now()}`, raw: "", ppm: "", unit: "ppm", doseStatus: "ok", resolved: null, added: true },
      ]);

    return (
      <div className="vstack gap-2">
        {/* Hide the native number-input spinner arrows on the dose fields —
            users type the ppm value directly. */}
        <style>
          {".fim-no-spin::-webkit-outer-spin-button,.fim-no-spin::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}.fim-no-spin{-moz-appearance:textfield;appearance:textfield}"}
        </style>
        {/* Source summary */}
        <div
          className="hstack"
          style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}
        >
          <div className="hstack" style={{ gap: 8, alignItems: "center", minWidth: 0 }}>
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: 5,
                flexShrink: 0,
                background: "var(--accent-soft)",
                color: "var(--accent-ink)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid var(--accent-line)",
              }}
            >
              <Icon name={value.method === "clipboard" ? "copy" : "table"} size={13} />
            </span>
            <div className="vstack" style={{ gap: 1, lineHeight: 1.25, minWidth: 0 }}>
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {value.method === "clipboard" ? "Pasted from clipboard" : value.source}
              </span>
              <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
                {rows.length} ingredient{rows.length === 1 ? "" : "s"} · {total.toFixed(1)} ppm total
              </span>
            </div>
          </div>
          <div className="hstack" style={{ gap: 8 }}>
            <button type="button" className="btn xs ghost" onClick={reImport}>
              <Icon name="x" size={10} />
              Re-import
            </button>
          </div>
        </div>

        {/* Unresolved gate — must be cleared before continuing */}
        {!hideUnresolvedWarning && unresolved > 0 && (
          <div style={{ background: "#fdf6e5", border: "1px solid #e7c97a", borderRadius: 8, padding: "10px 12px" }}>
            <div className="hstack" style={{ gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: "#7c5813", flexShrink: 0, marginTop: 1 }}>
                <Icon name="alert" size={13} />
              </span>
              <div className="vstack" style={{ gap: 6, flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12, color: "#5b3f0d", fontWeight: 600 }}>
                  {unresolved} ingredient{unresolved === 1 ? "" : "s"} couldn't be matched
                </span>
                <span style={{ fontSize: 11.5, color: "#7c5813", lineHeight: 1.5 }}>
                  Resolve each to an ingredient, remove the row, or define it in your Substance Library.
                  You can't start designing until every row is matched.
                </span>
                <div className="hstack" style={{ gap: 8, marginTop: 2 }}>
                  <button type="button" className="btn xs" onClick={onOpenLibrary}>
                    <Icon name="box" size={11} />
                    Define in Substance Library
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!hideUnresolvedWarning && needsDose > 0 && (
          <div style={{ background: "#fdf6e5", border: "1px solid #e7c97a", borderRadius: 8, padding: "10px 12px" }}>
            <div className="hstack" style={{ gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: "#7c5813", flexShrink: 0, marginTop: 1 }}>
                <Icon name="alert" size={13} />
              </span>
              <div className="vstack" style={{ gap: 4, flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12, color: "#5b3f0d", fontWeight: 600 }}>
                  {needsDose} ingredient{needsDose === 1 ? "" : "s"} need{needsDose === 1 ? "s" : ""} a dose in ppm
                </span>
                <span style={{ fontSize: 11.5, color: "#7c5813", lineHeight: 1.5 }}>
                  We can only interpret doses in ppm or mg/kg. Rows we couldn't convert are
                  highlighted in red (a volumetric unit like mL/L, or missing units) — enter
                  each in ppm to continue.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div style={{ border: "1px solid var(--line)", borderRadius: 8, overflow: "visible", background: "var(--bg)" }}>
          {/* Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.45fr 116px 34px",
              alignItems: "center",
              gap: 10,
              padding: "7px 12px",
              background: "var(--bg-sunk)",
              borderBottom: "1px solid var(--line)",
            }}
          >
            <span className="mono" style={{ fontSize: 9, color: "var(--ink-4)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Raw name
            </span>
            <span className="mono" style={{ fontSize: 9, color: "var(--ink-4)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Resolved ingredient
            </span>
            <span className="mono" style={{ fontSize: 9, color: "var(--ink-4)", letterSpacing: "0.1em", textTransform: "uppercase", textAlign: "right" }}>
              Dose (ppm)
            </span>
            <span />
          </div>

          {rows.map((r, i) => {
            const flagged = r.doseStatus === "unconvertible";
            return (
              <div
                key={r.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1.45fr 116px 34px",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 12px",
                  borderBottom: i === rows.length - 1 ? "none" : "1px solid var(--line-2)",
                  position: "relative",
                }}
              >
                {/* Raw source — parsed name + the user's source CAS/FEMA (dull). */}
                <RawCell row={r} />

                {/* Resolved — live-search picker */}
                <ResolvedCell
                  row={r}
                  onResolve={(substance) => setResolved(r.id, substance)}
                  onOpenLibrary={onOpenLibrary}
                />

                {/* Dose — number + unit inline; red when we couldn't convert it */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 5,
                    minWidth: 0,
                    background: "var(--bg-sunk)",
                    border: "1px solid var(--line)",
                    borderRadius: 5,
                    padding: "0 8px",
                  }}
                >
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={r.ppm}
                    placeholder={r.ppm === "" ? "enter ppm" : undefined}
                    title={
                      flagged
                        ? r.rawUnit
                          ? `Your "${r.rawUnit}" can't be converted to ppm automatically — change the value to ppm manually.`
                          : "You haven't provided units — enter a ppm value."
                        : r.ppm === ""
                          ? "No dose found in the source — enter a value in ppm."
                          : undefined
                    }
                    onChange={(e) => {
                      const n = parseFloat(e.target.value);
                      setPpm(r.id, e.target.value === "" || Number.isNaN(n) ? "" : n);
                    }}
                    className="mono fim-no-spin"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: 12,
                      color: flagged ? "var(--err, #c33)" : "var(--ink)",
                      background: "transparent",
                      border: "none",
                      outline: "none",
                      padding: "6px 0",
                      textAlign: "right",
                      fontFeatureSettings: "'tnum'",
                    }}
                  />
                  <span
                    className="mono"
                    title={flagged && r.rawUnit ? r.rawUnit : "ppm"}
                    style={{
                      fontSize: 10,
                      color: flagged ? "var(--err, #c33)" : "var(--ink-4)",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {flagged && r.rawUnit ? r.rawUnit : "ppm"}
                  </span>
                </div>

                {/* Remove */}
                <button
                  type="button"
                  className="btn xs ghost"
                  onClick={() => removeRow(r.id)}
                  title="Remove row"
                  style={{ padding: "4px 5px", justifySelf: "center" }}
                >
                  <Icon name="trash" size={11} />
                </button>
              </div>
            );
          })}

          {rows.length === 0 && (
            <div style={{ padding: "20px 12px", textAlign: "center", color: "var(--ink-4)", fontSize: 12 }}>
              No ingredients. Add one below or re-import.
            </div>
          )}
        </div>

        <div className="hstack" style={{ justifyContent: "space-between", alignItems: "center", gap: 10 }}>
          <button type="button" className="btn xs" onClick={addRow}>
            <Icon name="plus" size={11} />
            Add ingredient
          </button>
          <span
            className="hstack"
            style={{ gap: 6, fontSize: 11, color: "var(--ink-4)", lineHeight: 1.5, alignItems: "center" }}
          >
            <Icon name="info" size={11} />
            <span>Click a resolved ingredient to search and re-assign.</span>
          </span>
        </div>
      </div>
    );
  }

  // ── Idle: single unified dropzone ──
  return (
    <div className="vstack gap-2">
      {error && (
        <div
          style={{
            fontSize: 12,
            color: "var(--err, #c33)",
            background: "#fdecec",
            border: "1px solid #f1c9c9",
            borderRadius: 6,
            padding: "8px 10px",
          }}
        >
          {error}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) startFile(f);
          e.target.value = "";
        }}
      />
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer?.files?.[0];
          if (f) startFile(f);
        }}
        style={{
          padding: compact ? "16px 14px" : "30px 22px",
          borderRadius: 10,
          textAlign: "center",
          cursor: "pointer",
          border: `1.5px dashed ${drag ? "var(--accent-ink)" : "var(--line-strong)"}`,
          background: drag ? "var(--accent-soft)" : "var(--bg-sunk)",
          transition: "background 100ms ease, border-color 100ms ease",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: compact ? 6 : 10,
        }}
      >
        <span
          style={{
            width: 40,
            height: 40,
            borderRadius: 9,
            background: drag ? "var(--accent-ink)" : "var(--panel)",
            color: drag ? "var(--accent-soft)" : "var(--accent-ink)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1px solid ${drag ? "var(--accent-ink)" : "var(--accent-line)"}`,
          }}
        >
          <Icon name="upload" size={19} />
        </span>
        <div className="vstack" style={{ gap: 3, alignItems: "center" }}>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: "var(--ink)" }}>
            Drop a spreadsheet, paste, or select a file
          </div>
          <div style={{ fontSize: 11.5, color: "var(--ink-4)", lineHeight: 1.5 }}>
            Drag tabular data here, or copy ingredient + dose rows and paste anywhere on the page.
          </div>
        </div>
        <div className="hstack" style={{ gap: 8, marginTop: 2, alignItems: "center" }}>
          <button
            type="button"
            className="btn xs"
            onClick={(e) => {
              e.stopPropagation();
              fileRef.current?.click();
            }}
          >
            <Icon name="file" size={11} />
            Select file
          </button>
          <span className="chip" style={{ background: "var(--bg)" }}>
            <Icon name="copy" size={10} />
            Paste anywhere
          </span>
        </div>
        <div className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
          .xlsx · .csv · .tsv
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ResolvedCell — shows the chosen substance for one raw ingredient and lets
// the user search the catalog + library to (re-)assign it.
// ─────────────────────────────────────────────────────────────────────

// Compound vs mixture type icon — mirrors the Substance Library picker
// (LibrarySelect) so the resolved ingredient reads the same across the flow.
function TypeIcon({ kind }: { kind: ResolvedSubstance["kind"] }) {
  const mix = kind === "mixture";
  return (
    <span
      title={mix ? "Mixture / extract" : "Compound"}
      style={{
        width: 20,
        height: 20,
        borderRadius: 5,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: mix ? "var(--signal-soft)" : "var(--accent-soft)",
        color: mix ? "#7c5813" : "var(--accent-ink)",
        border: `1px solid ${mix ? "#e7c97a" : "var(--accent-line)"}`,
      }}
    >
      <Icon name={mix ? "molecule" : "atom"} size={11} />
    </span>
  );
}

// Treat LLM placeholder values ("Not listed", "N/A", "-", …) as absent so we
// never render an empty/placeholder identifier tag.
function realId(v: string | null | undefined): string | null {
  if (!v) return null;
  const t = v.trim();
  if (!t) return null;
  if (["not listed", "none", "n/a", "na", "unknown", "-", "—", "null"].includes(t.toLowerCase())) {
    return null;
  }
  return t;
}

// Identifier chip — mirrors the Substance Library design system (the IdChip
// palette). The RESOLVED (authoritative)
// identifier uses the kind palette (CAS accent, FEMA amber); the user's RAW source
// identifiers render `dull` (neutral) so they recede as provenance.
const ID_PALETTE: Record<"CAS" | "FEMA", { bg: string; bd: string; fg: string }> = {
  CAS: { bg: "var(--accent-soft)", bd: "var(--accent-line)", fg: "var(--accent-ink)" },
  FEMA: { bg: "#f6e8c8", bd: "#e7c97a", fg: "#7c5813" },
};
const ID_DULL = { bg: "var(--bg-sunk)", bd: "var(--line)", fg: "var(--ink-4)" };

function IdChip({
  kind,
  value,
  small = false,
  dull = false,
}: {
  kind: "CAS" | "FEMA";
  value: string;
  small?: boolean;
  dull?: boolean;
}) {
  const c = dull ? ID_DULL : ID_PALETTE[kind];
  return (
    <span
      className="mono"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: small ? "1px 5px" : "2px 7px",
        borderRadius: 3,
        background: c.bg,
        border: `1px solid ${c.bd}`,
        color: c.fg,
        fontSize: small ? 9.5 : 10,
        letterSpacing: 0,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      <span style={{ opacity: dull ? 0.55 : 0.7, fontWeight: 600 }}>{kind}</span>
      <span>{value}</span>
    </span>
  );
}

// Resolved-ingredient identifier: CAS by default, FEMA fallback when a compound
// has no CAS. Mixtures carry neither → renders nothing.
function ResolvedId({ cas, fema }: { cas: string | null; fema?: string | null }) {
  const c = realId(cas);
  const f = realId(fema);
  if (c) return <IdChip kind="CAS" value={c} small />;
  if (f) return <IdChip kind="FEMA" value={f} small />;
  return null;
}

// RAW source cell — the user's parsed name + their source CAS/FEMA as dull
// (neutral) chips. Absent/placeholder identifiers render no tag.
function RawCell({ row }: { row: FormulaImportRow }) {
  if (row.added) {
    return (
      <span
        className="mono"
        style={{
          fontSize: 12,
          color: "var(--ink-4)",
          fontStyle: "italic",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          paddingLeft: 7,
        }}
      >
        Added manually
      </span>
    );
  }
  const cas = realId(row.cas);
  const fema = realId(row.fema);
  return (
    <div className="vstack" style={{ gap: 4, minWidth: 0, paddingLeft: 7 }}>
      <span
        title={row.raw || undefined}
        style={{
          fontSize: 12.5,
          fontWeight: 500,
          color: "var(--ink)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.name || row.raw || "—"}
      </span>
      {(cas || fema) && (
        <div className="hstack" style={{ gap: 5, flexWrap: "wrap" }}>
          {cas && <IdChip kind="CAS" value={cas} small dull />}
          {fema && <IdChip kind="FEMA" value={fema} small dull />}
        </div>
      )}
    </div>
  );
}

function ResolvedOption({
  s,
  selected,
  onPick,
}: {
  s: ResolvedSubstance;
  selected: boolean;
  onPick: (s: ResolvedSubstance) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(s)}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "7px 10px",
        textAlign: "left",
        borderRadius: 5,
        background: selected ? "var(--accent-soft)" : "transparent",
        border: `1px solid ${selected ? "var(--accent-line)" : "transparent"}`,
        cursor: "pointer",
        marginBottom: 2,
      }}
      onMouseEnter={(e) => {
        if (!selected) e.currentTarget.style.background = "var(--bg-sunk)";
      }}
      onMouseLeave={(e) => {
        if (!selected) e.currentTarget.style.background = "transparent";
      }}
    >
      <TypeIcon kind={s.kind} />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 12.5,
          fontWeight: 500,
          color: "var(--ink)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {s.name}
      </span>
      <ResolvedId cas={s.cas} fema={s.fema} />
      {selected && (
        <span style={{ color: "var(--accent-ink)", flexShrink: 0 }}>
          <Icon name="check" size={14} />
        </span>
      )}
    </button>
  );
}

function sameSubstance(a: ResolvedSubstance | null, b: ResolvedSubstance): boolean {
  return !!a && a.ndbTable === b.ndbTable && a.rowId === b.rowId;
}

function ResolvedCell({
  row,
  onResolve,
  onOpenLibrary,
}: {
  row: FormulaImportRow;
  onResolve: (substance: ResolvedSubstance) => void;
  onOpenLibrary?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState<ResolvedSubstance[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Debounced live search against the catalog + the user's library.
  useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (query.length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    const t = window.setTimeout(() => {
      void searchImportSubstances(query, "all", ctrl.signal)
        .then(({ items }) => {
          if (!ctrl.signal.aborted) {
            setResults(items);
            setLoading(false);
          }
        })
        .catch(() => {
          if (!ctrl.signal.aborted) {
            setResults([]);
            setLoading(false);
          }
        });
    }, 250);
    return () => {
      window.clearTimeout(t);
      ctrl.abort();
    };
  }, [q, open]);

  const chosen = row.resolved;

  const pick = (s: ResolvedSubstance) => {
    onResolve(s);
    setOpen(false);
    setQ("");
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", minWidth: 0 }}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "5px 8px",
          borderRadius: 6,
          cursor: "pointer",
          textAlign: "left",
          border: `1px solid ${chosen ? "var(--line)" : "#e7c97a"}`,
          background: chosen ? "var(--bg-sunk)" : "#fbf6e8",
        }}
      >
        {chosen ? (
          <>
            <TypeIcon kind={chosen.kind} />
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 12.5,
                fontWeight: 500,
                color: "var(--ink)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {chosen.name}
            </span>
            <ResolvedId cas={chosen.cas} fema={chosen.fema} />
          </>
        ) : (
          <span
            className="hstack"
            style={{ gap: 6, flex: 1, color: "#7c5813", fontSize: 12, fontWeight: 500 }}
          >
            <Icon name="alert" size={12} />
            Choose ingredient
          </span>
        )}
        <span
          style={{
            color: "var(--ink-4)",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 120ms ease",
          }}
        >
          <Icon name="chevron" size={11} />
        </span>
      </button>

      {/* Popover */}
      {open && (
        <div
          className="slide-up"
          style={{
            position: "absolute",
            top: "calc(100% + 5px)",
            left: 0,
            zIndex: 60,
            width: 340,
            maxWidth: "min(340px, 86vw)",
            background: "var(--panel)",
            border: "1px solid var(--line-strong)",
            borderRadius: 8,
            boxShadow: "var(--shadow-lift)",
            overflow: "hidden",
          }}
        >
          {/* Search */}
          <div style={{ padding: 8, borderBottom: "1px solid var(--line)", position: "relative" }}>
            <span style={{ position: "absolute", left: 17, top: 16, color: "var(--ink-4)" }}>
              <Icon name="search" size={12} />
            </span>
            <input
              autoFocus
              className="input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search your library and our catalog…"
              style={{ fontSize: 12, padding: "6px 10px 6px 30px" }}
            />
          </div>

          <div style={{ maxHeight: 280, overflowY: "auto", padding: 4 }}>
            {q.trim().length === 0 ? (
              <div style={{ padding: "20px 12px", textAlign: "center", color: "var(--ink-4)", fontSize: 11.5 }}>
                Type a name or CAS to search the full library.
              </div>
            ) : loading ? (
              <div style={{ padding: "20px 12px", textAlign: "center", color: "var(--ink-4)", fontSize: 12 }}>
                Searching…
              </div>
            ) : results.length === 0 ? (
              <div style={{ padding: "20px 12px", textAlign: "center", color: "var(--ink-4)", fontSize: 12 }}>
                No ingredients matched “{q}”.
              </div>
            ) : (
              results.map((s) => (
                <ResolvedOption
                  key={`${s.ndbTable}:${s.rowId}`}
                  s={s}
                  selected={sameSubstance(chosen, s)}
                  onPick={pick}
                />
              ))
            )}
          </div>

          {/* Footer — define a brand-new substance in the library */}
          <div style={{ borderTop: "1px solid var(--line)", padding: 6, background: "var(--panel-2)" }}>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenLibrary?.();
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 10px",
                border: 0,
                borderRadius: 5,
                background: "transparent",
                textAlign: "left",
                color: "var(--ink-2)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-sunk)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 4,
                  background: "var(--accent-soft)",
                  color: "var(--accent-ink)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid var(--accent-line)",
                  flexShrink: 0,
                }}
              >
                <Icon name="plus" size={11} />
              </span>
              <span style={{ flex: 1, fontSize: 12, fontWeight: 500 }}>Define new in Substance Library</span>
              <Icon name="external" size={11} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
